import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Container,
  Typography,
  TextField,
  Paper,
  Button,
  MenuItem,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon, Edit as EditIcon, ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { Patient, MedicalRecordItem } from '../types';
import {
  getPatients,
  upsertPatient,
  getPatientByIdStore,
  ensureMedicalRecord,
  addRecord,
  updateRecord,
  deleteRecord,
  getMedicalRecordByPatientIdStore,
} from '../utils/dataStore';

const genders = ['Männlich', 'Weiblich', 'Divers'];

const ScenarioEditorPage: React.FC = () => {
  const navigate = useNavigate();
  const { patientId } = useParams<{ patientId: string }>();

  const existingPatient = useMemo(() => (patientId ? getPatientByIdStore(patientId) : undefined), [patientId]);
  const [patient, setPatient] = useState<Patient>(
    existingPatient || {
      id: `patient-${Date.now()}`,
      name: '',
      age: 30,
      gender: 'Männlich',
      address: '',
      primaryPhysician: '',
      insuranceNumber: '',
      bloodType: '',
      avatar: '',
    }
  );

  const [records, setRecords] = useState<MedicalRecordItem[]>(() => getMedicalRecordByPatientIdStore(patient.id)?.records || []);

  useEffect(() => {
    if (patientId) {
      const mr = getMedicalRecordByPatientIdStore(patientId);
      setRecords(mr?.records || []);
    }
  }, [patientId]);

  // Dialog state for add/edit record
  const [recordDialogOpen, setRecordDialogOpen] = useState(false);
  const [editingRecord, setEditingRecord] = useState<MedicalRecordItem | null>(null);
  const [recordForm, setRecordForm] = useState<Omit<MedicalRecordItem, 'id'>>({
    date: new Date().toISOString().slice(0, 10),
    type: 'session',
    title: '',
    description: '',
    severity: undefined,
  });

  const openNewRecordDialog = () => {
    setEditingRecord(null);
    setRecordForm({ date: new Date().toISOString().slice(0, 10), type: 'session', title: '', description: '' });
    setRecordDialogOpen(true);
  };

  const openEditRecordDialog = (rec: MedicalRecordItem) => {
    setEditingRecord(rec);
    const { id, ...rest } = rec;
    setRecordForm(rest);
    setRecordDialogOpen(true);
  };

  const closeRecordDialog = () => setRecordDialogOpen(false);

  const handleSavePatient = () => {
    if (!patient.name.trim()) return;
    upsertPatient(patient);
    ensureMedicalRecord(patient.id); // make sure record container exists
  };

  const handleSaveRecord = () => {
    if (!patient.name.trim()) {
      // Save patient first if new
      handleSavePatient();
    }

    if (editingRecord) {
      const updated: MedicalRecordItem = { id: editingRecord.id, ...recordForm };
      updateRecord(patient.id, updated);
    } else {
      addRecord(patient.id, recordForm);
    }
    const updatedList = getMedicalRecordByPatientIdStore(patient.id)?.records || [];
    setRecords(updatedList);
    setRecordDialogOpen(false);
  };

  const handleDeleteRecord = (id: string) => {
    deleteRecord(patient.id, id);
    const updatedList = getMedicalRecordByPatientIdStore(patient.id)?.records || [];
    setRecords(updatedList);
  };

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <IconButton onClick={() => navigate(-1)} aria-label="Zurück">
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h5" sx={{ ml: 1 }}>
          {existingPatient ? 'Szenario bearbeiten' : 'Neues Szenario erstellen'}
        </Typography>
      </Box>

      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Allgemeine Infos
        </Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
          <Box>
            <TextField
              label="Name"
              fullWidth
              value={patient.name}
              onChange={(e) => setPatient({ ...patient, name: e.target.value })}
            />
          </Box>
          <Box>
            <TextField
              label="Alter"
              type="number"
              fullWidth
              value={patient.age}
              onChange={(e) => setPatient({ ...patient, age: Number(e.target.value) })}
            />
          </Box>
          <Box>
            <TextField
              select
              label="Geschlecht"
              fullWidth
              value={patient.gender}
              onChange={(e) => setPatient({ ...patient, gender: e.target.value })}
            >
              {genders.map(g => (
                <MenuItem key={g} value={g}>{g}</MenuItem>
              ))}
            </TextField>
          </Box>
          <Box sx={{ gridColumn: '1 / -1' }}>
            <TextField
              label="Adresse"
              fullWidth
              value={patient.address || ''}
              onChange={(e) => setPatient({ ...patient, address: e.target.value })}
            />
          </Box>
          <Box>
            <TextField
              label="Hausarzt"
              fullWidth
              value={patient.primaryPhysician || ''}
              onChange={(e) => setPatient({ ...patient, primaryPhysician: e.target.value })}
            />
          </Box>
          <Box>
            <TextField
              label="Versichertennummer"
              fullWidth
              value={patient.insuranceNumber || ''}
              onChange={(e) => setPatient({ ...patient, insuranceNumber: e.target.value })}
            />
          </Box>
          <Box>
            <TextField
              label="Blutgruppe"
              fullWidth
              value={patient.bloodType || ''}
              onChange={(e) => setPatient({ ...patient, bloodType: e.target.value })}
            />
          </Box>
          <Box sx={{ gridColumn: '1 / -1' }}>
            <TextField
              label="Avatar URL"
              fullWidth
              value={patient.avatar || ''}
              onChange={(e) => setPatient({ ...patient, avatar: e.target.value })}
            />
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
          <Button variant="contained" onClick={handleSavePatient}>Speichern</Button>
          <Button variant="outlined" onClick={() => navigate(`/chat/${patient.id}`)} disabled={!patient.name.trim()}>
            In Chat ansehen
          </Button>
        </Box>
      </Paper>

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">Einträge</Typography>
          <Button startIcon={<AddIcon />} variant="contained" onClick={openNewRecordDialog} disabled={!patient.name.trim()}>
            Eintrag hinzufügen
          </Button>
        </Box>
        <Divider sx={{ my: 2 }} />
        <List>
          {records.map((r) => (
            <ListItem key={r.id} divider>
              <ListItemText
                primary={`${r.title} — ${r.type}`}
                secondary={`${r.date} • ${r.description}${r.severity ? ' • ' + r.severity : ''}`}
              />
              <ListItemSecondaryAction>
                <IconButton edge="end" onClick={() => openEditRecordDialog(r)} aria-label="Bearbeiten">
                  <EditIcon />
                </IconButton>
                <IconButton edge="end" onClick={() => handleDeleteRecord(r.id)} aria-label="Löschen" sx={{ ml: 1 }}>
                  <DeleteIcon />
                </IconButton>
              </ListItemSecondaryAction>
            </ListItem>
          ))}
          {records.length === 0 && (
            <Typography color="text.secondary">Noch keine Einträge vorhanden.</Typography>
          )}
        </List>
      </Paper>

      {/* Record Dialog */}
      <Dialog open={recordDialogOpen} onClose={closeRecordDialog} fullWidth maxWidth="sm">
        <DialogTitle>{editingRecord ? 'Eintrag bearbeiten' : 'Eintrag hinzufügen'}</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
            <Box>
              <TextField
                label="Datum"
                type="date"
                fullWidth
                value={recordForm.date}
                onChange={(e) => setRecordForm({ ...recordForm, date: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Box>
            <Box>
              <TextField
                label="Titel"
                fullWidth
                value={recordForm.title}
                onChange={(e) => setRecordForm({ ...recordForm, title: e.target.value })}
              />
            </Box>
            <Box>
              <TextField
                select
                label="Typ"
                fullWidth
                value={recordForm.type}
                onChange={(e) => setRecordForm({ ...recordForm, type: e.target.value as MedicalRecordItem['type'] })}
              >
                <MenuItem value="session">Sitzung</MenuItem>
                <MenuItem value="treatment">Behandlung</MenuItem>
                <MenuItem value="diagnosis">Diagnose</MenuItem>
                <MenuItem value="medication">Medikament</MenuItem>
                <MenuItem value="symptom">Symptom</MenuItem>
              </TextField>
            </Box>
            <Box>
              <TextField
                select
                label="Schweregrad"
                fullWidth
                value={recordForm.severity || ''}
                onChange={(e) => setRecordForm({ ...recordForm, severity: (e.target.value || undefined) as any })}
              >
                <MenuItem value="">Keiner</MenuItem>
                <MenuItem value="low">Niedrig</MenuItem>
                <MenuItem value="medium">Mittel</MenuItem>
                <MenuItem value="high">Hoch</MenuItem>
              </TextField>
            </Box>
            <Box sx={{ gridColumn: '1 / -1' }}>
              <TextField
                label="Beschreibung"
                fullWidth
                multiline
                minRows={3}
                value={recordForm.description}
                onChange={(e) => setRecordForm({ ...recordForm, description: e.target.value })}
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeRecordDialog}>Abbrechen</Button>
          <Button variant="contained" onClick={handleSaveRecord}>{editingRecord ? 'Speichern' : 'Hinzufügen'}</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default ScenarioEditorPage;
